/**
 * Audit API Service
 * Following PRD Phase 4 specifications (lines 478-485)
 */

import { AuditLog, AuditFilters, AuditExportOptions, AuditStats } from '@/types/audit';

export interface AuditLogsResponse {
  data: AuditLog[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  stats: AuditStats;
}

/**
 * Fetch audit logs with filtering and pagination
 */
export const fetchAuditLogs = async (filters: AuditFilters): Promise<AuditLogsResponse> => {
  // For demo purposes, return mock data
  const mockLogs: AuditLog[] = generateMockAuditLogs(filters);
  const total = 150; // Mock total count
  const page = filters.page || 1;
  const limit = filters.limit || 50;

  return {
    data: mockLogs,
    total,
    page,
    limit,
    hasMore: page * limit < total,
    stats: {
      totalLogs: total,
      successCount: 120,
      errorCount: 20,
      pendingCount: 10,
      averageDuration: 245
    }
  };

  // Real implementation would be:
  // const response = await apiClient.get('/audit/logs', { params: filters });
  // return response.data;
};

/**
 * Fetch a single audit log by ID
 */
export const fetchAuditLogById = async (id: string): Promise<AuditLog> => {
  // For demo purposes, return mock data
  return generateMockAuditLog(id);

  // Real implementation would be:
  // const response = await apiClient.get(`/audit/logs/${id}`);
  // return response.data;
};

/**
 * Export audit logs in specified format
 */
export const exportAuditLogs = async (options: AuditExportOptions): Promise<Blob> => {
  // For demo purposes, create a mock export
  const mockData = generateMockExportData(options);

  let blob: Blob;
  switch (options.format) {
    case 'csv':
      blob = new Blob([mockData.csv], { type: 'text/csv' });
      break;
    case 'json':
      blob = new Blob([mockData.json], { type: 'application/json' });
      break;
    case 'pdf':
      // For PDF, we'd use a library like jsPDF in real implementation
      blob = new Blob([mockData.pdf], { type: 'application/pdf' });
      break;
    default:
      throw new Error('Unsupported export format');
  }

  return blob;

  // Real implementation would be:
  // const response = await apiClient.post('/audit/export', options, {
  //   responseType: 'blob'
  // });
  // return response.data;
};

// Helper functions for mock data generation

function generateMockAuditLogs(filters: AuditFilters): AuditLog[] {
  const actions: AuditLog['action'][] = ['Plan Created', 'Step Completed', 'Tool Used', 'User Action', 'System Event'];
  const statuses: AuditLog['status'][] = ['success', 'error', 'pending', 'warning'];
  const tools = ['ReadVoucher', 'CreateLedger', 'GenerateReport', 'ExportData', 'SyncTally'];
  
  const logs: AuditLog[] = [];
  const count = filters.limit || 50;
  let i = 0;
  
  while (logs.length < count) {
    const action = actions[Math.floor(Math.random() * actions.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const hasToolData = action === 'Tool Used';
    
    const log: AuditLog = {
      id: `log-${Date.now()}-${i}`,
      timestamp: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      action,
      description: generateDescription(action, status),
      status,
      duration: Math.floor(Math.random() * 1000) + 50,
      userId: 'user-123',
      sessionId: `session-${Math.floor(Math.random() * 1000)}`,
      metadata: {
        ip: '192.168.1.100',
        userAgent: 'Mozilla/5.0...'
      }
    };
    
    if (hasToolData) {
      const toolName = tools[Math.floor(Math.random() * tools.length)];
      log.tool = {
        name: toolName,
        payload: generateToolPayload(toolName),
        result: generateToolResult(toolName, status)
      };
    }
    
    // Apply filters
    if (filters.search && !log.description.toLowerCase().includes(filters.search.toLowerCase())) {
      i++;
      continue;
    }
    if (filters.actions?.length && !filters.actions.includes(log.action)) {
      i++;
      continue;
    }
    if (filters.status?.length && !filters.status.includes(log.status)) {
      i++;
      continue;
    }
    
    logs.push(log);
    i++;
  }
  
  return logs;
}

function generateMockAuditLog(id: string): AuditLog {
  return {
    id,
    timestamp: new Date().toISOString(),
    action: 'Tool Used',
    description: 'Generated detailed profit and loss report for Q4 2024',
    tool: {
      name: 'GenerateReport',
      payload: {
        reportType: 'profit_loss',
        period: 'Q4 2024',
        company: 'ABC Corp'
      },
      result: {
        success: true,
        reportId: 'rpt-12345',
        totalRevenue: 1250000,
        totalExpenses: 980000,
        netProfit: 270000
      }
    },
    status: 'success',
    duration: 342,
    userId: 'user-123',
    sessionId: 'session-456',
    metadata: {
      ip: '192.168.1.100',
      userAgent: 'Mozilla/5.0...',
      tallyVersion: '9.6.7'
    }
  };
}

function generateDescription(action: AuditLog['action'], _status: AuditLog['status']): string {
  const descriptions = {
    'Plan Created': [
      'Created execution plan for monthly sales report',
      'Initialized batch voucher creation workflow',
      'Set up automated invoice generation process'
    ],
    'Step Completed': [
      'Completed data validation for 150 vouchers',
      'Finished ledger reconciliation process',
      'Exported customer statements successfully'
    ],
    'Tool Used': [
      'Generated profit and loss statement',
      'Created payment voucher for vendor',
      'Synchronized inventory data with Tally'
    ],
    'User Action': [
      'User logged in successfully',
      'Updated company configuration',
      'Changed report preferences'
    ],
    'System Event': [
      'Automatic backup completed',
      'Connection to Tally established',
      'Cache cleared successfully'
    ]
  };
  
  const options = descriptions[action];
  return options[Math.floor(Math.random() * options.length)];
}

function generateToolPayload(toolName: string): Record<string, any> {
  const payloads: Record<string, any> = {
    ReadVoucher: {
      voucherType: 'Payment',
      voucherNumber: 'PV-2024-001',
      company: 'ABC Corp'
    },
    CreateLedger: {
      ledgerName: 'New Customer',
      group: 'Sundry Debtors',
      openingBalance: 0
    },
    GenerateReport: {
      reportType: 'balance_sheet',
      asOnDate: '2024-12-31',
      format: 'detailed'
    },
    ExportData: {
      dataType: 'vouchers',
      dateRange: { from: '2024-01-01', to: '2024-12-31' },
      format: 'excel'
    },
    SyncTally: {
      syncType: 'full',
      entities: ['ledgers', 'vouchers', 'inventory']
    }
  };
  
  return payloads[toolName] || {};
}

function generateToolResult(toolName: string, status: AuditLog['status']): Record<string, any> {
  if (status === 'error') {
    return {
      success: false,
      error: 'Connection timeout',
      errorCode: 'CONN_TIMEOUT'
    };
  }
  
  const results: Record<string, any> = {
    ReadVoucher: {
      success: true,
      data: {
        amount: 50000,
        ledger: 'ABC Suppliers',
        narration: 'Payment for Invoice INV-123'
      }
    },
    CreateLedger: {
      success: true,
      ledgerId: 'LED-12345',
      message: 'Ledger created successfully'
    },
    GenerateReport: {
      success: true,
      reportUrl: '/reports/balance-sheet-2024.pdf',
      generatedAt: new Date().toISOString()
    },
    ExportData: {
      success: true,
      recordsExported: 1523,
      fileSize: '2.4 MB',
      downloadUrl: '/exports/vouchers-2024.xlsx'
    },
    SyncTally: {
      success: true,
      syncedEntities: {
        ledgers: 245,
        vouchers: 1523,
        inventory: 89
      },
      duration: '2m 34s'
    }
  };
  
  return results[toolName] || { success: true };
}

function generateMockExportData(options: AuditExportOptions) {
  const logs = generateMockAuditLogs(options.filters || {});
  
  // CSV format
  const csvHeaders = ['ID', 'Timestamp', 'Action', 'Description', 'Status', 'Duration'];
  const csvRows = logs.map(log => [
    log.id,
    log.timestamp,
    log.action,
    log.description,
    log.status,
    log.duration?.toString() || ''
  ]);
  const csv = [csvHeaders, ...csvRows].map(row => row.join(',')).join('\\n');
  
  // JSON format
  const json = JSON.stringify(logs, null, 2);
  
  // Mock PDF content
  const pdf = 'Mock PDF content - in real implementation, use jsPDF';
  
  return { csv, json, pdf };
}